import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import {
  Zap, PlayCircle, PauseCircle, ChevronRight, Clock, Shield, ClipboardCheck,
  AlertTriangle, CheckCircle2, Loader2, Bell, Calendar as CalendarIcon,
  FileText, ListFilter, ClipboardList,
} from 'lucide-react';
import AutomationRunDetailsModal from '@/components/AutomationRunDetailsModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const ICON_BY_ID = {
  compliance_weekly: Shield,
  certification_daily: ClipboardCheck,
  assessment_reminder: Bell,
  renewal_quarterly_digest: FileText,
  automation_health_digest: ClipboardList,
};

export default function AutomationsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pausing, setPausing] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/automations`, { headers });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Admin or HR role required');
        navigate('/dashboard');
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load automations');
      }
    } finally { setLoading(false); }
  };

  const pauseAll = async () => {
    if (!window.confirm('Pause all scheduled automations? You can re-enable them individually from the automation pages.')) return;
    setPausing(true);
    try {
      await axios.post(`${API}/admin/automations/pause-all`, {}, { headers });
      toast.success('All automations paused');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pause failed');
    } finally { setPausing(false); }
  };

  if (loading) return <div className="p-8 text-center text-[#9CA3AF]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!data) return null;

  const enabled = data.enabled_count;
  const total = data.total;

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="automations-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#002FA7]" />System Automations
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Scheduled background jobs running on the KStreak platform. {enabled} of {total} currently active.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-automations-btn">
            Refresh
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => navigate('/admin/automations/runs')}
            data-testid="view-full-log-btn"
          >
            <ListFilter className="w-4 h-4 mr-1" /> View full log
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={pauseAll}
            disabled={pausing || enabled === 0}
            className="text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/5"
            data-testid="pause-all-btn"
          >
            {pausing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PauseCircle className="w-4 h-4 mr-1" />}
            Pause All
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3" data-testid="automation-summary">
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Total automations</p>
            <p className="text-2xl font-bold text-[#0A0A0A] mt-1">{total}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Active</p>
            <p className="text-2xl font-bold text-[#34C759] mt-1 flex items-center gap-2">
              {enabled}
              {enabled > 0 && <CheckCircle2 className="w-4 h-4" />}
            </p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Paused</p>
            <p className={`text-2xl font-bold mt-1 flex items-center gap-2 ${total - enabled > 0 ? 'text-[#FF9500]' : 'text-[#9CA3AF]'}`}>
              {total - enabled}
              {total - enabled > 0 && <PauseCircle className="w-4 h-4" />}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Automation cards */}
      <div className="space-y-3" data-testid="automation-list">
        {data.automations.map(a => <AutomationRow key={a.id} automation={a} navigate={navigate} onRunClick={setSelectedRunId} />)}
      </div>

      <AutomationRunDetailsModal
        runId={selectedRunId}
        open={!!selectedRunId}
        onOpenChange={(v) => { if (!v) setSelectedRunId(null); }}
      />
    </div>
  );
}

function AutomationRow({ automation, navigate, onRunClick }) {
  const Icon = ICON_BY_ID[automation.id] || Zap;
  const nextRun = automation.enabled ? computeNextRun(automation) : null;
  const lastRunAgo = automation.last_run_at
    ? formatDistanceToNow(new Date(automation.last_run_at), { addSuffix: true })
    : null;
  const recentRuns = automation.recent_runs || [];
  const failureCount = recentRuns.filter(r => r.status === 'failure').length;

  return (
    <Card className="border-[#E5E7EB]" data-testid={`automation-${automation.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${automation.enabled ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-[#F3F4F6] text-[#9CA3AF]'}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">{automation.name}</h3>
              <Badge className={`text-[9px] ${automation.enabled ? 'bg-[#34C759]/15 text-[#34C759]' : 'bg-[#9CA3AF]/15 text-[#9CA3AF]'}`} data-testid={`automation-${automation.id}-status`}>
                {automation.enabled ? 'Active' : 'Paused'}
              </Badge>
              <span className="text-[10px] text-[#9CA3AF]">{automation.cadence}</span>
              {automation.email_enabled !== undefined && automation.email_enabled && (
                <Badge className="text-[9px] bg-[#002FA7]/10 text-[#002FA7]">Email on</Badge>
              )}
              {failureCount > 0 && (
                <Badge className="text-[9px] bg-[#FF3B30]/15 text-[#FF3B30] flex items-center gap-0.5" data-testid={`automation-${automation.id}-failures`}>
                  <AlertTriangle className="w-2.5 h-2.5" />{failureCount} recent failure{failureCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-[#4B5563] mt-1">{automation.description}</p>
            <div className="grid md:grid-cols-4 gap-2 mt-3 text-[10px]">
              <DataCell label="Schedule" value={formatSchedule(automation)} />
              <DataCell label="Last run" value={lastRunAgo || '—'} sub={automation.last_run_triggered_by || ''} />
              <DataCell label="Next run" value={nextRun || 'Paused'} />
              <DataCell label="Last outcome" value={formatOutcome(automation)} />
            </div>
            {/* Run-history sparkline */}
            <div className="mt-3 flex items-center gap-2" data-testid={`automation-${automation.id}-sparkline`}>
              <span className="text-[9px] text-[#9CA3AF] uppercase tracking-wider w-14">History</span>
              <div className="flex gap-0.5 flex-1">
                {Array.from({ length: 20 }).map((_, i) => {
                  const runIndex = i - (20 - recentRuns.length);
                  const run = runIndex >= 0 ? recentRuns[runIndex] : null;
                  const color = !run ? '#E5E7EB' : run.status === 'success' ? '#34C759' : '#FF3B30';
                  const title = run
                    ? `${format(new Date(run.created_at), 'MMM d HH:mm')} · ${run.status} · ${run.triggered_by || '—'}${run.error ? ` · ${run.error.substring(0, 80)}` : ''}${run.duration_ms ? ` · ${run.duration_ms}ms` : ''} — click for full detail`
                    : 'No run yet';
                  if (run && run.run_id) {
                    return (
                      <button
                        key={i}
                        type="button"
                        aria-label={title}
                        title={title}
                        onClick={(e) => { e.stopPropagation(); onRunClick && onRunClick(run.run_id); }}
                        className="h-4 flex-1 rounded-sm cursor-pointer transition-transform hover:scale-y-125 hover:ring-1 hover:ring-[#002FA7]/40"
                        style={{ backgroundColor: color, minWidth: 2 }}
                        data-testid={`sparkline-bar-${automation.id}-${i}`}
                      />
                    );
                  }
                  return <div key={i} className="h-4 flex-1 rounded-sm" style={{ backgroundColor: color, minWidth: 2 }} title={title} />;
                })}
              </div>
              <span className="text-[9px] text-[#9CA3AF]">{recentRuns.length}/20</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/automations/runs?automation_id=${automation.id}`); }}
                className="text-[9px] text-[#002FA7] hover:underline font-semibold"
                data-testid={`automation-${automation.id}-view-all`}
              >
                View all →
              </button>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate(automation.href)} className="flex-shrink-0 text-[11px]" data-testid={`automation-${automation.id}-configure`}>
            Configure <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DataCell({ label, value, sub }) {
  return (
    <div>
      <p className="text-[#9CA3AF] uppercase tracking-wider" style={{ fontSize: '9px' }}>{label}</p>
      <p className="text-[#0A0A0A] font-medium mt-0.5">{value}</p>
      {sub && <p className="text-[#9CA3AF] text-[9px] truncate">{sub}</p>}
    </div>
  );
}

function formatSchedule(a) {
  const hour = String(a.hour ?? 0).padStart(2, '0');
  if (a.cadence === 'Weekly') {
    return `${DAY_LABELS[a.day_of_week] || 'Mon'} @ ${hour}:00 UTC`;
  }
  if (a.cadence === 'Quarterly') {
    return `Jan/Apr/Jul/Oct 1 @ ${hour}:00 UTC`;
  }
  return `Daily @ ${hour}:00 UTC`;
}

function computeNextRun(a) {
  const hour = a.hour ?? 0;
  const now = new Date();
  if (a.cadence === 'Quarterly') {
    const candidates = [];
    for (const yr of [now.getUTCFullYear(), now.getUTCFullYear() + 1]) {
      for (const mo of [0, 3, 6, 9]) {
        const d = new Date(Date.UTC(yr, mo, 1, hour, 0, 0));
        if (d > now) candidates.push(d);
      }
    }
    candidates.sort((a, b) => a - b);
    return candidates.length ? format(candidates[0], "MMM d, yyyy HH:mm 'UTC'") : '—';
  }
  const candidate = new Date();
  candidate.setUTCHours(hour, 0, 0, 0);
  if (a.cadence === 'Weekly') {
    const targetDow = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 }[a.day_of_week || 'mon'];
    const diff = (targetDow - candidate.getUTCDay() + 7) % 7;
    candidate.setUTCDate(candidate.getUTCDate() + diff);
    if (candidate <= now) candidate.setUTCDate(candidate.getUTCDate() + 7);
  } else if (candidate <= now) {
    candidate.setUTCDate(candidate.getUTCDate() + 1);
  }
  return format(candidate, "MMM d, HH:mm 'UTC'");
}

function formatOutcome(a) {
  if (a.id === 'compliance_weekly') {
    const s = a.last_run_summary || {};
    if (!s.compliance_rate && s.compliance_rate !== 0) return '—';
    return `${s.compliance_rate}% compliant`;
  }
  if (a.id === 'certification_daily') {
    const d = a.last_run_dispatched || {};
    const total = (d.expired || 0) + (d['7'] || 0) + (d['30'] || 0) + (d['60'] || 0);
    if (!d.total_scanned && d.total_scanned !== 0) return '—';
    return `${total} alert${total !== 1 ? 's' : ''} / ${d.total_scanned || 0} scanned`;
  }
  if (a.id === 'assessment_reminder') {
    const d = a.last_run_dispatched || {};
    if (!d.total_users && d.total_users !== 0) return '—';
    return `${d.reminded || 0} reminded / ${d.total_users || 0} users`;
  }
  if (a.id === 'renewal_quarterly_digest') {
    if (!a.last_run_at) return '—';
    if (a.last_run_error) return `Failed: ${a.last_run_error.substring(0, 40)}`;
    return `${a.last_run_recipients || 0} HR/admin emailed`;
  }
  if (a.id === 'automation_health_digest') {
    if (!a.last_run_at) return '—';
    const regs = a.last_run_regressions || 0;
    const runs = a.last_run_total_runs || 0;
    return `${runs} runs · ${regs} regression${regs === 1 ? '' : 's'}`;
  }
  return '—';
}
